/**
 * SSO через Drafter ID (B-1973). Вход/регистрация — только на `id.drafter.ru`.
 * У Акселератора своей формы нет.
 *
 * После входа на `id.drafter.ru` в браузере лежит общая httpOnly-cookie `token` на
 * `Domain=.drafter.ru` (JS её не видит). Браузер сам прикладывает её к запросам на
 * `api.accelerator.drafter.ru` при `credentials: 'include'`; бэкенд Акселератора
 * читает и расшифровывает cookie в `exchange-drafter` и выдаёт локальный access
 * (+ провижинит Lead для audience=cabinet).
 *
 * - `exchangeViaCookie()` — обменять cookie на локальный сеанс (без Bearer).
 * - `redirectToDrafterId()` — уйти на форму входа IdP с адресом возврата.
 */

import axios from 'axios';

const API_BASE: string = (import.meta.env.VITE_API_URL as string) || '/api';
const DRAFTER_ID_URL: string =
  (import.meta.env.VITE_DRAFTER_ID_URL as string) || 'https://id.drafter.ru';
// Админка акселератора — куда уводим ментора (B-2175) и куда ведёт ссылка
// «Админка» в шапке кабинета (B-2517). SSO-cookie `.drafter.ru` авторизует и там.
export const ADMIN_URL: string =
  (import.meta.env.VITE_ADMIN_ACCELERATOR_URL as string) || 'https://admin.accelerator.drafter.ru';

export interface DrafterUserSummary {
  id: number;
  name: string | null;
  surname: string | null;
  roles: string[];
  is_admin: boolean;
  // B-2175/B-2221: ментор = верифицированная анкета `Mentor` монолита (не роль).
  // Признак считает бэкенд, фронт только уводит ментора без лида в админку.
  is_mentor: boolean;
}

export interface ExchangeResult {
  accessToken: string;
  /**
   * `null` — штатно: с B-2175 лид не заводится ментору без существующего лида
   * (мусорные WEB_FORM-лиды в канбане). У студента лид есть всегда.
   */
  leadId: string | null;
  /**
   * По контракту поле обязательное. `?` — страховка границы: 200 без `user`
   * (рассинхрон деплоя, HTML от прокси) не должен ронять вход `TypeError`'ом
   * внутри `try` и подменять успешный логин экраном «Не удалось войти».
   */
  user?: DrafterUserSummary;
}

export interface ExchangeOptions {
  /**
   * B-2378: ментор пришёл в кабинет учиться (пункт «ЛК ученика» в админке).
   * Бэкенд заводит ему лид, как любому участнику; без флага ментору лид не
   * заводится (B-2175). Повторные обмены флаг не несут — лид уже есть.
   */
  asStudent?: boolean;
}

/**
 * Обменять общую cookie Драфтера на локальный access кабинета (+ провижининг Lead).
 * `withCredentials` обязателен — иначе браузер не приложит cookie `.drafter.ru`.
 * 401 → валидной cookie нет → вызывающий редиректит в IdP.
 */
export async function exchangeViaCookie(options: ExchangeOptions = {}): Promise<ExchangeResult> {
  const body = options.asStudent
    ? { audience: 'cabinet', as_student: true }
    : { audience: 'cabinet' };
  const { data } = await axios.post<{
    access_token: string;
    lead_id: string | null;
    user?: DrafterUserSummary;
  }>(`${API_BASE}/v1/auth/exchange-drafter`, body, { withCredentials: true });
  return { accessToken: data.access_token, leadId: data.lead_id ?? null, user: data.user };
}

/** Уйти на форму входа `id.drafter.ru` с возвратом на текущий адрес. */
export function redirectToDrafterId(): void {
  const source = encodeURIComponent(window.location.href);
  window.location.href = `${DRAFTER_ID_URL}/auth?source=${source}`;
}

/**
 * Признак «ментор пришёл как ученик» в адресе входа: `/login-required?as=student`
 * (так ведёт пункт «ЛК ученика» из админки, B-2378).
 */
export const AS_STUDENT_PARAM = 'as';
export const AS_STUDENT_VALUE = 'student';

/** Увести ментора в админку акселератора (B-2175), минуя студенческий кабинет. */
export function redirectToAdmin(): void {
  window.location.assign(ADMIN_URL);
}

/**
 * Есть ли у участника доступ в админку — то же правило, что `hasPanelAccess`
 * в admin-frontend (`lib/panel-access.ts`): admin/super-admin или
 * верифицированный ментор. По нему кабинет показывает ссылку «Админка» (B-2517):
 * пункт «ЛК ученика» ведёт из админки в кабинет, а обратного пути не было.
 * Нет данных — нет ссылки.
 */
export function hasAdminPanelAccess(
  flags: { is_admin?: boolean; is_mentor?: boolean } | null | undefined,
): boolean {
  return Boolean(flags?.is_admin || flags?.is_mentor);
}
